import { Table, useMantineColorScheme } from "@mantine/core";
import {
  formatCompactNumber,
  formatEnUsValue,
  formatValueByChartTitle,
  formatTooltipValueByChartTitle,
  useCurrency,
} from "../../utils/helpers";
import "./synthesis.scss";
import PropTypes from "prop-types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const Synthesis = ({ data }) => {
  const { colorScheme } = useMantineColorScheme();
  const { t } = useTranslation();
  const currency = useCurrency();

  const descColor = colorScheme === "dark" ? "#d5d7e0" : "#626469";
  const headerAccent = colorScheme === "dark" ? "#a278c8" : "#5C1C96";
  const synthesisPayback = data?.payback;
  const synthesisRoi = data?.roi;

  const containerStyle = useMemo(
    () => ({
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
    }),
    []
  );

  const rowStyle = useMemo(
    () => ({
      display: "flex",
      justifyContent: "space-between",
      width: "100%",
      marginBottom: "16px",
    }),
    []
  );

  const cardStyle = useMemo(
    () => ({
      flex: "1",
      padding: "16px",
      paddingLeft: "0px",
      margin: "8px",
    }),
    []
  );

  const formatCurrency = (value) => {
    const numericValue = Number(value ?? 0);
    return {
      display: `${currency} ${formatCompactNumber(numericValue)}`,
      hover: `${currency} ${formatEnUsValue(numericValue)}`,
    };
  };
  const formatFixedCurrency = (value) => {
    const numericValue = Number(value ?? 0);
    return {
      display: `${currency} ${formatCompactNumber(numericValue)}`,
      hover: `${currency} ${formatEnUsValue(numericValue)}`,
    };
  };
  const formatWithUnit = (value, unit, title = "") => {
    if (typeof value === "string" && value.trim().toLowerCase() === "infinity") {
      const infinityText = "Infinity";
      if (!unit) {
        return {
          display: infinityText,
          hover: infinityText,
        };
      }
      const replacedUnit = unit.replace("$", currency);
      return {
        display: `${infinityText} ${replacedUnit}`,
        hover: `${infinityText} ${replacedUnit}`,
      };
    }

    const numericValue = Number(value ?? 0);
    const compactValue = formatValueByChartTitle(title, numericValue);
    const fullValue = formatTooltipValueByChartTitle(title, numericValue);
    if (!unit) {
      return {
        display: `${compactValue}`,
        hover: `${fullValue}`,
      };
    }
    // replace any $ in unit with dynamic currency
    const replacedUnit = unit.replace("$", currency);
    return {
      display: `${compactValue} ${replacedUnit}`,
      hover: `${fullValue} ${replacedUnit}`,
    };
  };

  const sections = useMemo(
    () => [
      {
        key: "capex",
        header: t("result.synthesis.capex.header"),
        description: t("result.synthesis.capex.description"),
        headerClass: "synth-top-header synth-large-header",
        descMinHeight: "18%",
        rows: [
          { label: t("result.synthesis.capex.pv"), value: formatCurrency(data?.pvCapex) },
          { label: t("result.synthesis.capex.genset"), value: formatCurrency(data?.gensetCapex) },
          { label: t("result.synthesis.capex.battery"), value: formatCurrency(data?.batteryCapex) },
          { label: t("result.synthesis.capex.controller"), value: formatCurrency(data?.controllerCapex) },
        ],
      },
      {
        key: "savings",
        header: t("result.synthesis.savings.header"),
        description: t("result.synthesis.savings.description"),
        headerClass: "synth-top-header synth-large-header",
        rows: [
          {
            label: t("result.synthesis.savings.electricBillReference"),
            value: formatCurrency(data?.refElectricBillCharge),
          },
          { label: t("result.synthesis.savings.electricBill"), value: formatCurrency(data?.electricBillCharge) },
        ],
      },
      {
        key: "npc",
        header: t("result.synthesis.npc.header"),
        description: t("result.synthesis.npc.description"),
        headerClass: "synth-top-header synth-large-header",
        rows: [
          { label: t("result.synthesis.npc.reference"), value: formatFixedCurrency(data?.refNetPresentCost) },
          { label: t("result.synthesis.npc.system"), value: formatFixedCurrency(data?.netPresentCost) },
        ],
      },
      {
        key: "lcoe",
        header: t("result.synthesis.lcoe.header"),
        description: t("result.synthesis.lcoe.description"),
        headerClass: "synth-bottom-header synth-large-header",
        headerColor: headerAccent,
        rows: [
          {
            label: t("result.synthesis.lcoe.reference"),
            value: formatWithUnit(
              data?.refLcoeUtility,
              "$/kWh",
              t("result.synthesis.lcoe.header")
            ),
          },
          {
            label: t("result.synthesis.lcoe.system"),
            value: formatWithUnit(
              data?.lcoeUtility,
              "$/kWh",
              t("result.synthesis.lcoe.header")
            ),
          },
        ],
      },
      {
        key: "co2",
        header: t("result.synthesis.co2.header"),
        description: t("result.synthesis.co2.description"),
        headerClass: "synth-bottom-header synth-large-header",
        headerColor: headerAccent,
        rows: [
          { label: t("result.synthesis.co2.reference"), value: formatWithUnit(data?.refCo2Emission, "t") },
          { label: t("result.synthesis.co2.system"), value: formatWithUnit(data?.co2Emission, "t") },
        ],
      },
      {
        key: "payback",
        header: t("result.synthesis.payback.header"),
        description: t("result.synthesis.payback.description"),
        headerClass: "synth-bottom-header synth-large-header",
        headerColor: headerAccent,
        descMinHeight: "24%",
        rows: [
          {
            label: t("result.synthesis.payback.label"),
            value: formatWithUnit(synthesisPayback, t("common.years")),
          },
          {
            label: t("result.synthesis.roi.label"),
            value: formatWithUnit(synthesisRoi, "%"),
          },
        ],
      },
    ],
    [data, headerAccent, synthesisPayback, synthesisRoi, t]
  );

  const groupedSections = useMemo(
    () => [sections.slice(0, 3), sections.slice(3)],
    [sections]
  );

  const renderTable = (rows) => (
    <Table>
      <thead>
        <tr>
          <th></th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.label}>
            <td>{row.label}</td>
            <td title={row.value?.hover}>{row.value?.display}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  return (
    <div
      className="indicator-downloadable"
      style={{ backgroundColor: colorScheme === "dark" ? "#111111" : "#fff" }}
    >
      <div
        style={{
          color: "#3dcd58",
          fontSize: "18px",
          borderBottom: "1px solid #ccc",
        }}
      >
        {t("result.synthesis.title")}
      </div>
      <div style={containerStyle}>
        {groupedSections.map((group) => (
          <div key={group[0].key} style={rowStyle}>
            {group.map((section) => (
              <div key={section.key} style={cardStyle}>
                <div
                  className={section.headerClass}
                  style={section.headerColor ? { color: section.headerColor } : undefined}
                >
                  {section.header}
                </div>
                <div
                  className="synth-small-header"
                  style={{
                    color: descColor,
                    minHeight: section.descMinHeight,
                  }}
                >
                  {section.description}
                </div>
                {renderTable(section.rows)}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

Synthesis.propTypes = {
  data: PropTypes.shape({
    batteryCapex: PropTypes.number,
    co2Emission: PropTypes.number,
    controllerCapex: PropTypes.number,
    electricBillCharge: PropTypes.number,
    gensetCapex: PropTypes.number,
    lcoeUtility: PropTypes.number,
    netPresentCost: PropTypes.number,
    payback: PropTypes.number,
    paybackDiscounted: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    PaybackDiscounted: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    pvCapex: PropTypes.number,
    refCo2Emission: PropTypes.number,
    refElectricBillCharge: PropTypes.number,
    refLcoeUtility: PropTypes.number,
    refNetPresentCost: PropTypes.number,
    roi: PropTypes.number,
    roiDiscounted: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ROIDiscounted: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

export default Synthesis;
